import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useGetEmployeeById, useUpdateEmployeeRoles } from '../../hooks/use-employees';
import { Employee, EmployeesPagingResult } from '../../types/employee.types';
import { RoleFormValues, RoleInfoStep } from '../employee-form/steps/role-info-step';
import { useTranslatedResolver } from '../employee-form/translate-form-errors';
import { employeeName } from '../../schemas/display-name';
import { useReportUnreportedError } from '@/state/query-client/hooks';

// Organization access comes straight from Blocks OS IAM (organizations + per-organization roles) and goes
// onto the login user, not the profile.
const roleFormSchema = z.object({
  OrganizationAccess: z
    .array(
      z.object({
        OrganizationId: z.string().min(1),
        RoleKeys: z.array(z.string()).min(1, { message: 'AT_LEAST_ONE_ROLE_REQUIRED' }),
      })
    )
    .min(1, { message: 'AT_LEAST_ONE_ORGANIZATION_REQUIRED' }),
});

export interface RoleAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The list row; its organizations and roles are read from IAM when the dialog opens. */
  employee: Employee | null;
}

/**
 * "Assign role" from the directory list: the organizations the person's login user has access to and
 * the roles held in each. Adding a person no longer asks for roles, so this is where they are granted.
 */
export const RoleAssignDialog = ({ open, onOpenChange, employee }: RoleAssignDialogProps) => {
  const { t } = useTranslation();
  // List rows do not carry IAM access; the full record does.
  const { data, isLoading } = useGetEmployeeById(open ? employee?.ItemId : undefined);
  const loaded = (data as { Employees?: EmployeesPagingResult } | undefined)?.Employees?.items?.[0];
  const { mutateAsync, isPending } = useUpdateEmployeeRoles();
  const reportError = useReportUnreportedError();

  const resolver = useTranslatedResolver<RoleFormValues>(roleFormSchema);
  const form = useForm<RoleFormValues>({ resolver, defaultValues: { OrganizationAccess: [] } });

  useEffect(() => {
    if (open && loaded) form.reset({ OrganizationAccess: loaded.OrganizationAccess ?? [] });
  }, [open, loaded, form]);

  const handleSubmit = async (values: RoleFormValues) => {
    if (!loaded) return;
    try {
      await mutateAsync({ employee: loaded, organizationAccess: values.OrganizationAccess });
      onOpenChange(false);
    } catch (error) {
      reportError(error);
    }
  };

  const name = employeeName(employee);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col overflow-y-hidden sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('ASSIGN_ROLE')}</DialogTitle>
          <DialogDescription>{name.trim()}</DialogDescription>
        </DialogHeader>

        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex min-h-0 flex-1 flex-col gap-5"
          >
            {/* The only scroll area: header and footer stay put, so the dialog never scrolls itself. */}
            <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
              {isLoading || !loaded ? (
                <Skeleton className="h-24 w-full rounded-md" />
              ) : (
                <RoleInfoStep />
              )}
            </div>

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isPending} disabled={isPending || !loaded}>
                {t('SAVE')}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
